import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import RecentWorkFlip from "../Components/RecentWorkStrip";

const containerVariants = {
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  exit: { opacity: 0, y: -30, transition: { duration: 0.3 } },
};

const works = [
  { src: "/documents/medical_thesis.pdf", type: "Thesis", file_type: "pdf" },
  { src: "/documents/research.pdf", type: "Research", file_type: "pdf" },
  { src: "/documents/mba.pdf", type: "Data Analysis & Interpretation ", file_type: "pdf" },
  { src: "/documents/ppt.pptx", type: " PowerPoint Presentations", file_type: "pdf" },
  
];

export default function HomePage() {
  const { scrollY } = useScroll();
  const yHero = useTransform(scrollY, [0, 300], [0, 100]); // Parallax effect
  const [heroLoaded, setHeroLoaded] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const img = new Image();
    img.src = "/images/hero_image.jpg";
    img.onload = () => setHeroLoaded(true);
  }, []);

  return (
    <motion.div
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="w-full px-0 py-4 max-w-screen-xl mx-auto space-y-12"
    >
      <motion.section
        style={{
          backgroundImage: `url('/images/home_banner.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          y: yHero,
        }}
        className={`relative h-[60vh] sm:h-[70vh] flex items-center justify-center text-white rounded-lg overflow-hidden shadow-xl transition-opacity duration-700 ${
          heroLoaded ? "opacity-100" : "opacity-100"
        }`}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <motion.div
          className="relative text-center z-10"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <motion.h1
            className="text-3xl sm:text-5xl font-extrabold mb-3 bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text"
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Fueling Ambitions One Chapter At A Time
          </motion.h1>
          <motion.p
            className="text-base sm:text-lg text-gray-200"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Making your academic journey stress-free with expert research, data analysis, writing, and presentation services.
          </motion.p>
          <motion.p
            className="font-sacramento text-3xl  mt-7 italic tracking-wide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.6 }}
          >
            by Scholerly Edge
          </motion.p>
        </motion.div>
      </motion.section>

      <div className="h-1 w-40 mx-auto bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-[1px]" />

      {/* ================= SERVICES SECTION ================= */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="w-full text-center"
      >
        <div className="relative inline-block mb-8">
          <div className="absolute inset-0 bg-white/30 backdrop-blur-md rounded-full px-6 py-2" />
          <h2 className="relative z-10 text-3xl sm:text-4xl font-cinzel font-semibold text-gray-800 tracking-wide">
            Services
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { name: "Thesis & Dissertation Writing", description : "Focused, scholarly, and organized—the ideal environment for deep work and critical thinking", icon: "📘", image: "/images/services/thesis.png" },
            { name: "Data Analysis & Interpretation", description : "A collaborative, high-tech setting that suggests precision, modern methodology, and the synthesis of complex information into clear conclusions", icon: "📊", image: "/images/services/data-presentation.png" },
            { name: "Research Paper Writing & Publication Support", description : "We refine your manuscript for logical flow, clarity, and technical accuracy.", icon: "📄", image: "/images/services/research-paper.png" },
            { name: "PowerPoint Presentations", description : "A clean, minimalist interface that prioritizes readability and high-end aesthetics.", icon: "🖥️ ", image: "/images/services/power-presentation.png" },
            { name: "Plagiarism Check & Removal", description : "Total peace of mind, knowing your manuscript meets the strictest ethical standards of any university or journal.", icon: "🔍 ", image: "/images/services/flagarism.png" },
            { name: "Citation & Formatting", description : "A highly organized, scholarly environment that signals attention to detail and academic rigor.", icon: "📝", image: "/images/services/cta.png" },
            
          ].map(({ name, icon, image, description }) => (
            <motion.div
              key={name}
              onClick={() => navigate(`/contact`)}
              className="group relative h-[200px] sm:h-[250px] lg:h-[280px] p-4 rounded-xl overflow-hidden shadow-md border border-transparent hover:border-purple-400 transition-all duration-300 hover:shadow-xxl"
              whileHover={{ scale: 1.02 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              style={{
                backgroundImage: `url(${image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-50 group-hover:bg-opacity-40 transition duration-300 z-0" />
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-300 to-red-300 opacity-0 group-hover:opacity-20 transition duration-300 z-0" />
              <div className="relative z-10 text-center text-white px-2">
                <div className="text-4xl sm:text-5xl mb-2">{icon}</div>
                <h3 className="text-lg sm:text-xl font-semibold mb-1">
                  {name}
                </h3>
                <p className="text-sm sm:text-base">
                    {description}
                </p>

              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      <div className="h-1 w-40 mx-auto bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-[1px]" />

      {/* ================= RECENT WORK SECTION ================= */}
      <section className="w-full text-center">
        <div className="relative inline-block mb-8">
          <div className="absolute inset-0 bg-white/30 backdrop-blur-md rounded-full px-6 py-2" />
          <h2 className="relative z-10 text-3xl sm:text-4xl font-cinzel font-semibold text-gray-800">
            Recent Work
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          viewport={{ once: true }}
        >
          <RecentWorkFlip works={works} />
          <div className="mt-6">
            <motion.a
                      href="/work"
                      className="inline-block px-6 py-3 text-white rounded shadow bg-gradient-to-r from-purple-500 via-pink-400 to-red-400 hover:from-purple-600 hover:via-pink-500 hover:to-red-500 transition"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >

              View All Work →
                    </motion.a>
          </div>
        </motion.div>
      </section>

      <div className="h-1 w-40 mx-auto bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-[1px]" />

    </motion.div>
  );
}
